import {
  __BaseInputProps,
  Checkbox,
  Flex,
  NumberInput,
  TagsInput,
  TextInput,
} from "@mantine/core"
import { FieldType } from "../../@client/Theasoft_Data"
import { RangePicker } from "../range-picker"

interface FieldFilterProps extends __BaseInputProps {
  fieldType: FieldType
  value: string[] | null | undefined
  onChange: (value: string[] | undefined) => void
  label?: string
}

export function FieldFilter({ fieldType, ...props }: FieldFilterProps) {
  const parser = new DOMParser()
  const xmlString = fieldType.settings
  const xmlDoc = parser.parseFromString(xmlString, "text/xml")

  const { dataType } = fieldType
  switch (dataType) {
    case "Bool":
      const boolValue = props.value?.[0] === "True"
      return (
        <Checkbox
          checked={boolValue}
          onChange={() => props.onChange(boolValue ? undefined : ["True"])}
        />
      )
    case "Date":
      const dateRange = props.value
      const start = dateRange?.[0]
      const end = dateRange?.[1]
      const startDate = start ? new Date(start) : undefined
      const endDate = end ? new Date(end) : undefined
      const handleChange = (start: Date | undefined, end: Date | undefined) => {
        if (!start && !end) {
          props.onChange(undefined)
          return
        }
        const newValue: string[] = []
        if (start) newValue[0] = start.toISOString()
        if (end) newValue[1] = end.toISOString()
        props.onChange(newValue)
      }
      return (
        <RangePicker
          clearable
          start={startDate}
          end={endDate}
          setStart={(newStart) => handleChange(newStart, endDate)}
          setEnd={(newEnd) => handleChange(startDate, newEnd)}
        />
      )
    case "Integer":
    case "Float":
      const numRange = props.value
      const numStart = numRange?.[0] ? Number(numRange[0]) : undefined
      const numEnd = numRange?.[1] ? Number(numRange[1]) : undefined

      const handleNumChange = (start?: number, end?: number) => {
        if (!start && !end) {
          props.onChange(undefined)
          return
        }
        const newValue: string[] = []
        if (start != null) newValue[0] = start.toString()
        if (end != null) newValue[1] = end.toString()
        props.onChange(newValue)
      }

      return (
        <Flex gap={"xs"}>
          <NumberInput
            step={dataType === "Float" ? 0.1 : 1}
            placeholder="Min"
            value={numStart}
            onChange={(value) => handleNumChange(value as number, numEnd)}
          />
          <NumberInput
            step={dataType === "Float" ? 0.1 : 1}
            placeholder="Max"
            value={numEnd}
            onChange={(value) => handleNumChange(numStart, value as number)}
          />
        </Flex>
      )
    case "TokenEdit":
    case "TextLookup":
    case "OptionLookup":
      const values = Array.from(
        xmlDoc.getElementsByTagName("FieldSettingsLookupValues")
      )
        .map((el) => el.getElementsByTagName("Value")[0]?.textContent)
        .filter((v) => typeof v === "string")

      return (
        <TagsInput
          {...props}
          data={values as string[]}
          value={props.value ?? undefined}
          onChange={(values) => props.onChange(values)}
          flex={1}
          splitChars={[" ", ","]}
          clearable
        />
      )
    default:
      return (
        <TextInput
          {...props}
          value={props.value || undefined}
          onChange={(e) => props.onChange([e.target.value])}
        />
      )
  }
}
